import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type {
  CategoryOption,
  FacilityOption,
  ProposalVersion,
  WorkOrderDetail,
} from '../api/types.js';
import {
  PRIORITIES,
  PRIORITY_LABELS,
  SUPPLIES_PAYMENT_METHODS,
  SUPPLIES_PAYMENT_METHOD_LABELS,
  type SuppliesPaymentMethod,
} from '../../shared/workflow.js';
import { previewProposalTotals } from '../lib/proposalMath.js';
import { formatCurrency } from '../lib/format.js';

interface SupplyItem {
  description: string;
  quantity: string;
  unitPrice: string;
}
const emptyItem = (): SupplyItem => ({ description: '', quantity: '1', unitPrice: '' });
const itemsToText = (items: SupplyItem[]) =>
  items
    .filter((item) => item.description.trim() !== '')
    .map((item) => `${item.description.trim()} — ${item.quantity.trim() || '1'}`)
    .join('\n');
import {
  Alert,
  Button,
  Card,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../components/ui.js';
import { AttachmentUploader } from '../components/AttachmentUploader.js';

interface CreateResponse {
  id: string;
  workOrderNumber: string;
}

export function WorkOrderCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const repeatId = searchParams.get('repeat');
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);
  const [items, setItems] = useState<SupplyItem[]>([emptyItem(), emptyItem(), emptyItem()]);
  const [quoteDrafted, setQuoteDrafted] = useState(false);

  const [form, setForm] = useState({
    facilityId: '',
    repairCategoryId: '',
    priority: 'routine',
    kind: 'repair' as 'repair' | 'supplies',
    itemsRequested: '',
    paymentMethod: '' as '' | SuppliesPaymentMethod,
    title: '',
    problemDescription: '',
    buildingArea: '',
    specificLocation: '',
    equipmentAsset: '',
    requestorName: '',
    requestorEmail: '',
    requestorPhone: '',
    requestedCompletionDate: '',
    additionalComments: '',
    emergencyCallout: false,
    emergencyAuthorizedBy: '',
    emergencyAuthorizedAt: '',
    emergencyJustification: '',
  });

  // "Repeat this order": pre-fill from a previous supplies work order.
  const { data: repeatSource } = useQuery({
    queryKey: ['work-order', repeatId, 'repeat'],
    queryFn: () => apiRequest<{ workOrder: WorkOrderDetail }>(`/api/work-orders/${repeatId}`),
    enabled: Boolean(repeatId),
  });
  const { data: repeatProposals } = useQuery({
    queryKey: ['work-order', repeatId, 'repeat-proposals'],
    queryFn: () =>
      apiRequest<{ versions: ProposalVersion[] }>(`/api/work-orders/${repeatId}/proposals`),
    enabled: Boolean(repeatId),
  });
  useEffect(() => {
    const versions = repeatProposals?.versions ?? [];
    const latest =
      versions.find((version) => version.status === 'approved') ?? versions[versions.length - 1];
    if (!latest?.costItems?.length) return;
    setItems(
      latest.costItems
        .filter((item) => item.visibility !== 'internal')
        .map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: (item.unitPriceCents / 100).toFixed(2),
        })),
    );
  }, [repeatProposals]);
  useEffect(() => {
    const source = repeatSource?.workOrder;
    if (!source) return;
    setForm((current) => ({
      ...current,
      facilityId: source.facilityId,
      repairCategoryId: source.categoryId,
      kind: source.kind,
      itemsRequested: source.itemsRequested ?? '',
      paymentMethod: source.paymentMethod ?? current.paymentMethod,
      title: source.title,
      problemDescription: source.kind === 'supplies' ? '' : source.problemDescription,
      buildingArea: source.buildingArea ?? '',
      specificLocation: source.specificLocation ?? '',
      requestorName: source.requestorName ?? current.requestorName,
      requestorEmail: source.requestorEmail ?? current.requestorEmail,
      requestorPhone: source.requestorPhone ?? current.requestorPhone,
    }));
  }, [repeatSource]);

  const { data: facilities, isLoading: facilitiesLoading } = useQuery({
    queryKey: ['reference', 'facilities'],
    queryFn: () =>
      apiRequest<{ facilities: FacilityOption[] }>('/api/reference/facilities').then(
        (result) => result.facilities,
      ),
  });

  const { data: categories } = useQuery({
    queryKey: ['reference', 'repair-categories'],
    queryFn: () =>
      apiRequest<{ categories: CategoryOption[] }>('/api/reference/repair-categories').then(
        (result) => result.categories,
      ),
  });

  const suppliesCategory = (categories ?? []).find((category) => category.code === 'SUPPLIES');
  const repairCategories = (categories ?? []).filter((category) => category.code !== 'SUPPLIES');

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiRequest<CreateResponse>('/api/work-orders', { method: 'POST', body: payload }),
    onSuccess: async (result) => {
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      if (form.kind === 'supplies') {
        // The priced item list is the quote: create it as the proposal draft so
        // the 9% markup is applied and it only needs "Submit to USPS".
        try {
          await apiRequest(`/api/work-orders/${result.id}/proposals`, {
            method: 'POST',
            body: {
              scopeOfWork: `Supplies / consumables order:\n${itemsToText(items)}`,
              costItems: items
                .filter((item) => item.description.trim() !== '')
                .map((item, index) => ({
                  costCategory: 'materials',
                  description: item.description.trim(),
                  quantity: item.quantity.trim() || '1',
                  unitPrice: Number(item.unitPrice || '0').toFixed(2),
                  visibility: 'customer_visible',
                  sortOrder: index,
                })),
            },
          });
          setQuoteDrafted(true);
        } catch {
          setQuoteDrafted(false);
        }
      }
      setCreatedId(result.id);
      setCreatedNumber(result.workOrderNumber);
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The work order could not be submitted. Please try again.',
      );
    },
  });

  const update = (field: keyof typeof form) => (value: string | boolean) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    create.mutate({
      facilityId: form.facilityId,
      repairCategoryId: form.repairCategoryId,
      priority: form.priority,
      kind: form.kind,
      itemsRequested: form.kind === 'supplies' ? itemsToText(items) : undefined,
      paymentMethod: form.kind === 'supplies' ? form.paymentMethod || undefined : undefined,
      title: form.title.trim(),
      problemDescription:
        form.kind === 'supplies'
          ? `Supplies / consumables order:\n${itemsToText(items)}`
          : form.problemDescription.trim(),
      buildingArea: form.buildingArea.trim() || undefined,
      specificLocation: form.specificLocation.trim() || undefined,
      equipmentAsset: form.equipmentAsset.trim() || undefined,
      requestorName: form.requestorName.trim(),
      requestorEmail: form.requestorEmail.trim(),
      requestorPhone: form.requestorPhone.trim() || undefined,
      requestedCompletionDate: form.requestedCompletionDate || undefined,
      additionalComments: form.additionalComments.trim() || undefined,
      emergencyCallout: form.emergencyCallout,
      emergencyAuthorizedBy: form.emergencyCallout
        ? form.emergencyAuthorizedBy.trim() || undefined
        : undefined,
      emergencyAuthorizedAt:
        form.emergencyCallout && form.emergencyAuthorizedAt
          ? new Date(form.emergencyAuthorizedAt).toISOString()
          : undefined,
      emergencyJustification: form.emergencyCallout
        ? form.emergencyJustification.trim() || undefined
        : undefined,
    });
  };

  if (facilitiesLoading) return <Spinner label="Loading facilities" />;

  if ((facilities ?? []).length === 0) {
    return (
      <>
        <PageHeader title="New work order" />
        <Alert variant="warning" title="No facilities are available to you">
          Your account is not yet authorized for any facility. Contact your administrator before
          submitting a request.
        </Alert>
      </>
    );
  }

  // Once the work order exists, photos attach to it — so the number is issued
  // before any file is uploaded, and an upload failure never loses the request.
  if (createdId) {
    return (
      <>
        <PageHeader
          title={`Work order ${createdNumber ?? ''} submitted`}
          subtitle="Add photos or documents now, or go straight to the work order."
        />
        <div className="flex flex-col gap-4">
          {form.kind === 'supplies' ? (
            <Alert variant={quoteDrafted ? 'success' : 'warning'}>
              {quoteDrafted
                ? 'The order has been entered and the priced item list saved as the quote (markup added). Next: open the work order and click "Save and submit to USPS" on the Proposal tab.'
                : 'The order has been entered, but the quote could not be saved automatically. Open the work order and enter the items and prices on the Proposal tab.'}
            </Alert>
          ) : (
            <Alert variant="success">
              Your request has been submitted and VGS has been notified.
            </Alert>
          )}
          <Card title="Attach photos or documents (optional)">
            <AttachmentUploader workOrderId={createdId} defaultCategory="request_photo" />
          </Card>
          <div className="flex gap-2">
            <Button
              onClick={() =>
                navigate(
                  form.kind === 'supplies'
                    ? `/work-orders/${createdId}?tab=proposals`
                    : `/work-orders/${createdId}`,
                )
              }
            >
              {form.kind === 'supplies' ? 'Go to the quote' : 'Go to work order'}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/work-orders')}>
              Back to list
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="New work order"
        subtitle="Describe the repair needed. A work order number is issued when you submit."
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert variant="error">{error}</Alert>}

        <Card title="Location and classification">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Facility" htmlFor="facilityId" required>
              <Select
                id="facilityId"
                required
                value={form.facilityId}
                onChange={(event) => update('facilityId')(event.target.value)}
              >
                <option value="">Select a facility…</option>
                {(facilities ?? []).map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.facilityCode} — {facility.name}
                  </option>
                ))}
              </Select>
            </Field>

            {form.kind === 'supplies' ? (
              <Field
                label="Category"
                htmlFor="repairCategoryId"
                required
                hint="Supplies orders always use the Supplies / consumables category."
              >
                <Select id="repairCategoryId" required value={form.repairCategoryId} disabled>
                  <option value={suppliesCategory?.id ?? ''}>
                    {suppliesCategory?.name ?? 'Supplies / consumables'}
                  </option>
                </Select>
              </Field>
            ) : (
              <Field label="Repair category" htmlFor="repairCategoryId" required>
                <Select
                  id="repairCategoryId"
                  required
                  value={form.repairCategoryId}
                  onChange={(event) => update('repairCategoryId')(event.target.value)}
                >
                  <option value="">Select a category…</option>
                  {repairCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field
              label="Priority"
              htmlFor="priority"
              required
              hint="Emergency is for conditions that are unsafe or stop operations."
            >
              <Select
                id="priority"
                required
                value={form.priority}
                onChange={(event) => update('priority')(event.target.value)}
              >
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {PRIORITY_LABELS[priority]}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Requested completion date" htmlFor="requestedCompletionDate">
              <TextInput
                id="requestedCompletionDate"
                type="date"
                value={form.requestedCompletionDate}
                onChange={(event) => update('requestedCompletionDate')(event.target.value)}
              />
            </Field>

            <Field label="Building or area" htmlFor="buildingArea">
              <TextInput
                id="buildingArea"
                value={form.buildingArea}
                onChange={(event) => update('buildingArea')(event.target.value)}
                placeholder="Dock, workroom floor, admin wing"
              />
            </Field>

            <Field label="Specific location" htmlFor="specificLocation">
              <TextInput
                id="specificLocation"
                value={form.specificLocation}
                onChange={(event) => update('specificLocation')(event.target.value)}
                placeholder="Door 4, second-floor restroom"
              />
            </Field>

            <Field label="Equipment or asset" htmlFor="equipmentAsset" className="sm:col-span-2">
              <TextInput
                id="equipmentAsset"
                value={form.equipmentAsset}
                onChange={(event) => update('equipmentAsset')(event.target.value)}
                placeholder="Asset tag or equipment name, if applicable"
              />
            </Field>
          </div>
        </Card>

        <Card title="What needs repair">
          <div className="flex flex-col gap-4">
            <Field
              label="Short title"
              htmlFor="title"
              required
              hint="One line, as it will appear in lists."
            >
              <TextInput
                id="title"
                required
                maxLength={200}
                value={form.title}
                onChange={(event) => update('title')(event.target.value)}
                placeholder="Overhead door will not close"
              />
            </Field>

            <Field
              label="Type of work order"
              htmlFor="kind"
              required
              hint="Supplies / consumables (toilet paper, paper towels, equipment purchases) are priced as they are entered, approved by USPS the same way, then ordered on the chosen payment method and marked received — no investigation, scheduling or PR/PO."
            >
              <Select
                id="kind"
                value={form.kind}
                onChange={(event) => {
                  const kind = event.target.value as 'repair' | 'supplies';
                  setForm({
                    ...form,
                    kind,
                    repairCategoryId:
                      kind === 'supplies'
                        ? (suppliesCategory?.id ?? '')
                        : form.repairCategoryId === suppliesCategory?.id
                          ? ''
                          : form.repairCategoryId,
                  });
                }}
              >
                <option value="repair">Repair / service</option>
                <option value="supplies">Supplies / consumables</option>
              </Select>
            </Field>

            {form.kind === 'supplies' ? (
              <>
                <Field
                  label="Items and prices"
                  htmlFor="item-0-description"
                  required
                  hint="One line per item with quantity and unit price. These lines become the quote USPS approves; the task order's markup is added automatically."
                >
                  <div className="flex flex-col gap-2">
                    {items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2">
                        <div className="col-span-6">
                          <TextInput
                            id={`item-${index}-description`}
                            placeholder="Item (e.g. Toilet paper, 2-ply, case of 80)"
                            value={item.description}
                            required={index === 0}
                            onChange={(event) =>
                              setItems((current) =>
                                current.map((row, i) =>
                                  i === index ? { ...row, description: event.target.value } : row,
                                ),
                              )
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <TextInput
                            aria-label="Quantity"
                            placeholder="Qty"
                            inputMode="decimal"
                            value={item.quantity}
                            onChange={(event) =>
                              setItems((current) =>
                                current.map((row, i) =>
                                  i === index ? { ...row, quantity: event.target.value } : row,
                                ),
                              )
                            }
                          />
                        </div>
                        <div className="col-span-3">
                          <TextInput
                            aria-label="Unit price"
                            placeholder="Unit price"
                            inputMode="decimal"
                            value={item.unitPrice}
                            required={item.description.trim() !== ''}
                            onChange={(event) =>
                              setItems((current) =>
                                current.map((row, i) =>
                                  i === index ? { ...row, unitPrice: event.target.value } : row,
                                ),
                              )
                            }
                          />
                        </div>
                        <div className="col-span-1 flex items-center">
                          {items.length > 1 && (
                            <Button
                              variant="ghost"
                              aria-label="Remove line"
                              onClick={() =>
                                setItems((current) => current.filter((_, i) => i !== index))
                              }
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between">
                      <Button
                        variant="secondary"
                        onClick={() => setItems((current) => [...current, emptyItem()])}
                      >
                        Add line
                      </Button>
                      <span className="text-sm text-slate-700">
                        Subtotal{' '}
                        {formatCurrency(
                          previewProposalTotals(
                            items.filter((item) => item.description.trim() !== ''),
                          ).subtotalCents,
                        )}{' '}
                        <span className="text-slate-500">+ markup (added on the quote)</span>
                      </span>
                    </div>
                  </div>
                </Field>
                <Field
                  label="Payment method"
                  htmlFor="paymentMethod"
                  required
                  hint="VGS only — USPS never sees this. Supplies orders skip Procurement Stage because the payment method is set here."
                >
                  <div className="flex flex-wrap gap-4" id="paymentMethod">
                    {SUPPLIES_PAYMENT_METHODS.map((method) => (
                      <label key={method} className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method}
                          required
                          checked={form.paymentMethod === method}
                          onChange={() => setForm({ ...form, paymentMethod: method })}
                        />
                        {SUPPLIES_PAYMENT_METHOD_LABELS[method]}
                      </label>
                    ))}
                  </div>
                </Field>
              </>
            ) : (
              <Field
                label="Problem description"
                htmlFor="problemDescription"
                required
                hint="What is wrong, when it started, and any effect on operations."
              >
                <TextArea
                  id="problemDescription"
                  required
                  minLength={10}
                  rows={6}
                  value={form.problemDescription}
                  onChange={(event) => update('problemDescription')(event.target.value)}
                />
              </Field>
            )}

            <Field label="Additional comments" htmlFor="additionalComments">
              <TextArea
                id="additionalComments"
                rows={3}
                value={form.additionalComments}
                onChange={(event) => update('additionalComments')(event.target.value)}
                placeholder="Access constraints, hours when work is possible, points of contact"
              />
            </Field>
          </div>
        </Card>

        <Card title="Emergency call-out">
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.emergencyCallout}
              onChange={(event) => update('emergencyCallout')(event.target.checked)}
            />
            <span>
              The work has already been performed (or is under way) because it could not wait for a
              priced proposal. USPS will approve the cost after the fact; the three-quote
              requirement is waived.
            </span>
          </label>
          {form.emergencyCallout && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Call-out authorized by" htmlFor="emergencyAuthorizedBy" required>
                <TextInput
                  id="emergencyAuthorizedBy"
                  required
                  value={form.emergencyAuthorizedBy}
                  onChange={(event) => update('emergencyAuthorizedBy')(event.target.value)}
                  placeholder="Name and role of the person who authorized the call-out"
                />
              </Field>
              <Field label="Authorized on" htmlFor="emergencyAuthorizedAt" required>
                <TextInput
                  id="emergencyAuthorizedAt"
                  type="datetime-local"
                  required
                  value={form.emergencyAuthorizedAt}
                  onChange={(event) => update('emergencyAuthorizedAt')(event.target.value)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Why it could not wait" htmlFor="emergencyJustification" required>
                  <TextArea
                    id="emergencyJustification"
                    rows={3}
                    required
                    value={form.emergencyJustification}
                    onChange={(event) => update('emergencyJustification')(event.target.value)}
                    placeholder="What was failing, the risk to safety or operations, and who was called out"
                  />
                </Field>
              </div>
            </div>
          )}
        </Card>

        <Card title="Point of contact">
          <p className="mb-3 text-sm text-slate-600">
            The USPS person who reported the problem — who VGS contacts about access and scheduling.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Name" htmlFor="requestorName" required>
              <TextInput
                id="requestorName"
                required
                value={form.requestorName}
                onChange={(event) => update('requestorName')(event.target.value)}
              />
            </Field>
            <Field label="Email" htmlFor="requestorEmail" required>
              <TextInput
                id="requestorEmail"
                type="email"
                required
                value={form.requestorEmail}
                onChange={(event) => update('requestorEmail')(event.target.value)}
              />
            </Field>
            <Field label="Phone" htmlFor="requestorPhone">
              <TextInput
                id="requestorPhone"
                type="tel"
                value={form.requestorPhone}
                onChange={(event) => update('requestorPhone')(event.target.value)}
              />
            </Field>
          </div>
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={create.isPending}>
            Submit work order
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/work-orders')}>
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
}
